import Downshift from "downshift";
import { getNames } from "i18n-iso-countries";
import { sortBy } from "lodash";
import type { FC } from "react";
import { useMemo } from "react";
import IconChevronDown from "~/assets/icons/chevron-down.svg";
import { Input } from "../Input";
import { TextMedium } from "../Typography";

type CountrySelectProps = {
  prohibitedCountryCodes: string[];
  initialValue: string | null;
};

type Option = {
  key: string;
  label: string;
  value: string;
};

export const CountrySelect: FC<CountrySelectProps> = function ({
  prohibitedCountryCodes,
  initialValue,
}) {
  const options = useMemo(() => {
    const names = getNames("en", { select: "official" });
    return sortBy(
      Object.keys(names)
        .filter((code) => !prohibitedCountryCodes.includes(code))
        .map<Option>((code) => ({
          label: names[code],
          key: names[code].toLowerCase(),
          value: code,
        })),
      "label"
    );
  }, [prohibitedCountryCodes]);

  return (
    <Downshift
      defaultHighlightedIndex={0}
      itemToString={(item) => (item ? item.label : "")}
      initialSelectedItem={options.find((item) => item.value === initialValue)}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        inputValue,
        highlightedIndex,
        selectedItem,
        openMenu,
        getRootProps,
      }) => (
        <div className="relative">
          <div
            className="inline-block w-full"
            {...getRootProps(undefined, { suppressRefError: true })}
          >
            <input
              name="countryCode"
              type="hidden"
              value={selectedItem?.value ?? ""}
            />
            <Input
              {...(getInputProps({}) as any)}
              trailingIcon={IconChevronDown}
              leadingIcon={
                selectedItem
                  ? `http://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedItem.value}.svg`
                  : undefined
              }
              leadingIconClassname="w-6 h-6"
              placeholder="Select country"
              onFocus={() => openMenu()}
              id="countryCode"
              className="w-full"
            />
          </div>
          <ul
            {...getMenuProps()}
            className="scrollbar absolute top-[60px] max-h-[200px] w-full overflow-auto rounded-lg drop-shadow-lg"
          >
            {isOpen
              ? filterOptions(options, inputValue).map((item, index) => (
                  <li
                    key={item.key}
                    className="block w-full bg-gray-100 px-4 py-3 text-lg font-medium"
                    {...getItemProps({
                      key: item.label,
                      index,
                      item,
                      style: {
                        backgroundColor:
                          highlightedIndex === index ? "#F9FAFB" : "white",
                        fontWeight: selectedItem === item ? "bold" : "normal",
                      },
                    })}
                  >
                    <TextMedium className="flex items-center gap-3 text-gray-700">
                      <img
                        alt=""
                        src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${item.value}.svg`}
                        className="h-6 w-6"
                        loading="lazy"
                      />
                      {item.label}
                    </TextMedium>{" "}
                  </li>
                ))
              : null}
          </ul>
        </div>
      )}
    </Downshift>
  );
};

const filterOptions = (options: Option[], inputValue: string | null) => {
  if (!inputValue) return options;
  const keyword = inputValue.toLowerCase();
  return options.filter((item) => item.key.includes(keyword));
};
